export enum BLOCK_TYPE {
  WARMUP = 'warmup',
  MAX_STRENGTH = 'max_strength',
  EXPLOSIVE = 'explosive',
  HYPERTROPHY = 'hypertrophy',
  PLYOMETRIC = 'plyometric',
  SPRINT = 'sprint',
  ACCESSORY = 'accessory',
  CONDITIONING = 'conditioning',
  COOLDOWN = 'cooldown',
  MOBILITY = 'mobility',
  PREHAB = 'prehab',
}

export const BLOCK_TYPE_COLORS: Record<BLOCK_TYPE, string> = {
  [BLOCK_TYPE.WARMUP]: '#00897B',
  [BLOCK_TYPE.MAX_STRENGTH]: '#D32F2F',
  [BLOCK_TYPE.EXPLOSIVE]: '#FF6F00',
  [BLOCK_TYPE.HYPERTROPHY]: '#1565C0',
  [BLOCK_TYPE.PLYOMETRIC]: '#F57F17',
  [BLOCK_TYPE.SPRINT]: '#00C853',
  [BLOCK_TYPE.ACCESSORY]: '#7B1FA2',
  [BLOCK_TYPE.CONDITIONING]: '#00838F',
  [BLOCK_TYPE.COOLDOWN]: '#78909C',
  [BLOCK_TYPE.MOBILITY]: '#4CAF50',
  [BLOCK_TYPE.PREHAB]: '#795548',
};

export const BLOCK_TYPE_LABELS: Record<BLOCK_TYPE, string> = {
  [BLOCK_TYPE.WARMUP]: 'Riscaldamento',
  [BLOCK_TYPE.MAX_STRENGTH]: 'Forza massima',
  [BLOCK_TYPE.EXPLOSIVE]: 'Esplosivo',
  [BLOCK_TYPE.HYPERTROPHY]: 'Ipertrofia',
  [BLOCK_TYPE.PLYOMETRIC]: 'Pliometria',
  [BLOCK_TYPE.SPRINT]: 'Sprint',
  [BLOCK_TYPE.ACCESSORY]: 'Accessori',
  [BLOCK_TYPE.CONDITIONING]: 'Condizionamento',
  [BLOCK_TYPE.COOLDOWN]: 'Defaticamento',
  [BLOCK_TYPE.MOBILITY]: 'Mobilità',
  [BLOCK_TYPE.PREHAB]: 'Prevenzione',
};

export enum LOAD_UNIT {
  KG = 'kg',
  LB = 'lb',
  PERCENT_1RM = '%1rm',
  PERCENT_BW = '%bw',
  PERCENT_MVIC = '%mvic',
  RPE = 'rpe',
  RIR = 'rir',
  MS = 'm/s',
  VELOCITY_ZONE = 'velocity_zone',
  PERCENT_VMAX = '%vmax',
  MIN = 'min',
  SEC = 'sec',
  M = 'm',
  KM = 'km',
  MIN_PER_KM = 'min_per_km',
  CM = 'cm',
  REPS = 'reps',
  KG_BW_SLED = 'kg_bw_sled',
  BPM = 'bpm',
  PERCENT_FC_MAX = '%fcmax',
  WATT = 'w',
  RSI = 'rsi',
  DEGREE = 'deg',
}

export const LOAD_UNIT_LABELS: Record<LOAD_UNIT, string> = {
  [LOAD_UNIT.KG]: 'kg',
  [LOAD_UNIT.LB]: 'lb',
  [LOAD_UNIT.PERCENT_1RM]: '%1RM',
  [LOAD_UNIT.PERCENT_BW]: '%BW',
  [LOAD_UNIT.PERCENT_MVIC]: '%MVIC',
  [LOAD_UNIT.RPE]: 'RPE',
  [LOAD_UNIT.RIR]: 'RIR',
  [LOAD_UNIT.MS]: 'm/s',
  [LOAD_UNIT.VELOCITY_ZONE]: 'Zona velocità',
  [LOAD_UNIT.PERCENT_VMAX]: '%vmax',
  [LOAD_UNIT.MIN]: 'min',
  [LOAD_UNIT.SEC]: 'sec',
  [LOAD_UNIT.M]: 'm',
  [LOAD_UNIT.KM]: 'km',
  [LOAD_UNIT.MIN_PER_KM]: 'min/km',
  [LOAD_UNIT.CM]: 'cm',
  [LOAD_UNIT.REPS]: 'reps',
  [LOAD_UNIT.KG_BW_SLED]: 'kg slitta',
  [LOAD_UNIT.BPM]: 'bpm',
  [LOAD_UNIT.PERCENT_FC_MAX]: '%FCmax',
  [LOAD_UNIT.WATT]: 'W',
  [LOAD_UNIT.RSI]: 'RSI',
  [LOAD_UNIT.DEGREE]: '°',
};

/**
 * Input mode dell'esercizio: determina la natura del lavoro e quali unità
 * di carico hanno senso. Mirror di INPUT_MODE backend (apps/backend/src/enums/profiling.ts)
 * + estensioni frontend per categorie sportive aggiuntive (cardio_machine, sled,
 * core_stability, agility) coerenti con la spec Input Mode v1.0.
 */
export enum EXERCISE_INPUT_MODE {
  STRENGTH = 'strength',
  ISOMETRIC = 'isometric',
  PLYOMETRIC = 'plyometric',
  SPRINT = 'sprint',
  CARDIO_FIELD = 'cardio_field',
  CARDIO_MACHINE = 'cardio_machine',
  SLED = 'sled',
  CORE_STABILITY = 'core_stability',
  MOBILITY = 'mobility',
  AGILITY = 'agility',
}

/**
 * Mappa input mode → unità di carico ammesse (Context Override per spec
 * Input Mode v1.0). RPE è trasversale.
 */
export const LOAD_UNITS_BY_INPUT_MODE: Record<EXERCISE_INPUT_MODE, LOAD_UNIT[]> = {
  [EXERCISE_INPUT_MODE.STRENGTH]: [
    LOAD_UNIT.KG, LOAD_UNIT.LB, LOAD_UNIT.PERCENT_1RM, LOAD_UNIT.PERCENT_BW,
    LOAD_UNIT.MS, LOAD_UNIT.RPE, LOAD_UNIT.RIR,
  ],
  [EXERCISE_INPUT_MODE.CARDIO_FIELD]: [
    LOAD_UNIT.MIN, LOAD_UNIT.SEC, LOAD_UNIT.M, LOAD_UNIT.KM,
    LOAD_UNIT.MIN_PER_KM, LOAD_UNIT.PERCENT_FC_MAX, LOAD_UNIT.BPM,
    LOAD_UNIT.WATT, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.CARDIO_MACHINE]: [
    LOAD_UNIT.MIN, LOAD_UNIT.SEC, LOAD_UNIT.M, LOAD_UNIT.KM,
    LOAD_UNIT.MIN_PER_KM, LOAD_UNIT.PERCENT_FC_MAX, LOAD_UNIT.BPM,
    LOAD_UNIT.WATT, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.SPRINT]: [
    LOAD_UNIT.SEC, LOAD_UNIT.M, LOAD_UNIT.MS,
    LOAD_UNIT.PERCENT_VMAX, LOAD_UNIT.VELOCITY_ZONE, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.PLYOMETRIC]: [
    LOAD_UNIT.CM, LOAD_UNIT.M, LOAD_UNIT.SEC, LOAD_UNIT.RSI, LOAD_UNIT.RPE,
    // Salti zavorrati (es. CMJ con carico): carico aggiunto in kg o %BW
    LOAD_UNIT.KG, LOAD_UNIT.PERCENT_BW,
    // Salti/hop rotazionali: angolo di rotazione come parametro (es. Rotation Jump 90°/180°)
    LOAD_UNIT.DEGREE,
  ],
  [EXERCISE_INPUT_MODE.ISOMETRIC]: [
    LOAD_UNIT.SEC, LOAD_UNIT.KG, LOAD_UNIT.PERCENT_BW,
    LOAD_UNIT.PERCENT_MVIC, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.SLED]: [
    LOAD_UNIT.KG_BW_SLED, LOAD_UNIT.PERCENT_BW, LOAD_UNIT.M, LOAD_UNIT.SEC,
    LOAD_UNIT.MS, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.CORE_STABILITY]: [
    LOAD_UNIT.SEC, LOAD_UNIT.REPS, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.MOBILITY]: [
    LOAD_UNIT.SEC, LOAD_UNIT.REPS, LOAD_UNIT.RPE,
  ],
  [EXERCISE_INPUT_MODE.AGILITY]: [
    LOAD_UNIT.SEC, LOAD_UNIT.M, LOAD_UNIT.RPE,
    // Cambi di direzione: angolo del taglio come parametro (es. Cut 45°/90°/180°)
    LOAD_UNIT.DEGREE,
  ],
};

/** Unità default (primaria) per input mode. */
export const DEFAULT_LOAD_UNIT_BY_INPUT_MODE: Record<EXERCISE_INPUT_MODE, LOAD_UNIT> = {
  [EXERCISE_INPUT_MODE.STRENGTH]: LOAD_UNIT.KG,
  [EXERCISE_INPUT_MODE.CARDIO_FIELD]: LOAD_UNIT.MIN,
  [EXERCISE_INPUT_MODE.CARDIO_MACHINE]: LOAD_UNIT.MIN,
  [EXERCISE_INPUT_MODE.SPRINT]: LOAD_UNIT.MS,
  [EXERCISE_INPUT_MODE.PLYOMETRIC]: LOAD_UNIT.CM,
  [EXERCISE_INPUT_MODE.ISOMETRIC]: LOAD_UNIT.SEC,
  [EXERCISE_INPUT_MODE.SLED]: LOAD_UNIT.KG_BW_SLED,
  [EXERCISE_INPUT_MODE.CORE_STABILITY]: LOAD_UNIT.SEC,
  [EXERCISE_INPUT_MODE.MOBILITY]: LOAD_UNIT.SEC,
  [EXERCISE_INPUT_MODE.AGILITY]: LOAD_UNIT.SEC,
};

/**
 * Helper: lista unità ammesse per il dropdown carico, filtrata in base
 * all'input mode dell'esercizio. Se inputMode mancante (esercizio legacy
 * senza classificazione) ritorna tutte le unità (override manuale completo).
 */
export function getLoadUnitsForInputMode(
  inputMode?: EXERCISE_INPUT_MODE | string,
): LOAD_UNIT[] {
  if (!inputMode) return Object.values(LOAD_UNIT);
  const mapped = LOAD_UNITS_BY_INPUT_MODE[inputMode as EXERCISE_INPUT_MODE];
  return mapped ?? Object.values(LOAD_UNIT);
}

/**
 * Helper: unità default per esercizio. Fallback kg se inputMode mancante.
 */
export function getDefaultLoadUnit(
  inputMode?: EXERCISE_INPUT_MODE | string,
): LOAD_UNIT {
  if (!inputMode) return LOAD_UNIT.KG;
  return DEFAULT_LOAD_UNIT_BY_INPUT_MODE[inputMode as EXERCISE_INPUT_MODE] ?? LOAD_UNIT.KG;
}

/**
 * Input mode che identificano un MEZZO ENDURANCE: corsa su campo, mezzi
 * macchina/bici/ergometri, e sprint (RSA). Per questi mezzi la riga NON usa la
 * griglia serie×reps×carico ma il COSTRUTTORE DI STEP (BUG A): volume/intensità/
 * recupero come dimensioni separate, intensità ancorata al profilo atleta.
 */
export const ENDURANCE_INPUT_MODES: ReadonlySet<EXERCISE_INPUT_MODE> = new Set([
  EXERCISE_INPUT_MODE.CARDIO_FIELD,
  EXERCISE_INPUT_MODE.CARDIO_MACHINE,
  EXERCISE_INPUT_MODE.SPRINT,
]);

/**
 * True se l'input mode dell'esercizio è un mezzo endurance → la riga deve
 * aprire il costruttore di step invece della griglia palestra.
 */
export function isEnduranceInputMode(
  inputMode?: EXERCISE_INPUT_MODE | string,
): boolean {
  if (!inputMode) return false;
  return ENDURANCE_INPUT_MODES.has(inputMode as EXERCISE_INPUT_MODE);
}

export enum EXERCISE_METHOD {
  STANDARD = 'standard',
  AMRAP = 'amrap',
  EMOM = 'emom',
  TABATA = 'tabata',
  CLUSTER = 'cluster',
  REST_PAUSE = 'rest_pause',
  TEMPO = 'tempo',
  ISOMETRIC = 'isometric',
  DROPSET = 'dropset',
  PYRAMID = 'pyramid',
}

export const EXERCISE_METHOD_LABELS: Record<EXERCISE_METHOD, string> = {
  [EXERCISE_METHOD.STANDARD]: 'Standard',
  [EXERCISE_METHOD.AMRAP]: 'AMRAP',
  [EXERCISE_METHOD.EMOM]: 'EMOM',
  [EXERCISE_METHOD.TABATA]: 'Tabata',
  [EXERCISE_METHOD.CLUSTER]: 'Cluster',
  [EXERCISE_METHOD.REST_PAUSE]: 'Rest-Pause',
  [EXERCISE_METHOD.TEMPO]: 'Tempo',
  [EXERCISE_METHOD.ISOMETRIC]: 'Isometrico',
  [EXERCISE_METHOD.DROPSET]: 'Drop Set',
  [EXERCISE_METHOD.PYRAMID]: 'Piramidale',
};

export enum WORKOUT_TYPE {
  SINGLE_DAY = 'single_day',
  MULTI_DAY = 'multi_day',
}

export enum ASSIGNMENT_TYPE {
  SCHEDULED = 'scheduled',
  PLAYLIST = 'playlist',
}

export enum DAY_MAPPING {
  CONSECUTIVE = 'consecutive',
  SPECIFIC_DAYS = 'specific_days',
  CUSTOM = 'custom',
}

export enum ASSIGNMENT_ORIGIN {
  CALENDAR_GLOBAL = 'calendar_global',
  TEAM_ATTIVITA = 'team_attivita',
  ATHLETE_ATTIVITA = 'athlete_attivita',
  LIBRARY = 'library',
  /** Scheda palestra creata dentro un blocco atletico di una sessione Campo. */
  FIELD_BLOCK = 'field_block',
}

export enum WORKOUT_LOG_STATUS {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  COMPLETED_LATE = 'completed_late',
}

export enum EXERCISE_LOG_STATUS {
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  PARTIAL = 'partial',
}

export enum FEEDBACK_TYPE {
  TROPPO_PESANTE = 'troppo_pesante',
  TROPPO_LEGGERO = 'troppo_leggero',
  DOLORE = 'dolore',
  TECNICA = 'tecnica',
  ALTRO = 'altro',
}

export enum PERIODICITY {
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  CUSTOM = 'custom',
}

export enum WORKOUT_CATEGORY {
  PERFORMANCE = 'performance',
  FITNESS = 'fitness',
  RTP_REHAB = 'rtp_rehab',
  PREHAB = 'prehab',
}

export const WORKOUT_CATEGORY_LABELS: Record<WORKOUT_CATEGORY, string> = {
  [WORKOUT_CATEGORY.PERFORMANCE]: 'Performance',
  [WORKOUT_CATEGORY.FITNESS]: 'Fitness / Generale',
  [WORKOUT_CATEGORY.RTP_REHAB]: 'RTP / Rehab',
  [WORKOUT_CATEGORY.PREHAB]: 'Prehab / Prevenzione',
};

export const WORKOUT_CATEGORY_COLORS: Record<WORKOUT_CATEGORY, string> = {
  [WORKOUT_CATEGORY.PERFORMANCE]: '#4a7cff',
  [WORKOUT_CATEGORY.FITNESS]: '#34d399',
  [WORKOUT_CATEGORY.RTP_REHAB]: '#f59e0b',
  [WORKOUT_CATEGORY.PREHAB]: '#a78bfa',
};

export enum PAIN_ZONES {
  GINOCCHIO_DX = 'ginocchio_dx',
  GINOCCHIO_SX = 'ginocchio_sx',
  CAVIGLIA_DX = 'caviglia_dx',
  CAVIGLIA_SX = 'caviglia_sx',
  HAMSTRING_DX = 'hamstring_dx',
  HAMSTRING_SX = 'hamstring_sx',
  QUADRICIPITE_DX = 'quadricipite_dx',
  QUADRICIPITE_SX = 'quadricipite_sx',
  POLPACCIO_DX = 'polpaccio_dx',
  POLPACCIO_SX = 'polpaccio_sx',
  PIEDE_DX = 'piede_dx',
  PIEDE_SX = 'piede_sx',
  SCHIENA_LOMBARE = 'schiena_lombare',
  SCHIENA_DORSALE = 'schiena_dorsale',
  ANCA_DX = 'anca_dx',
  ANCA_SX = 'anca_sx',
  INGUINE_DX = 'inguine_dx',
  INGUINE_SX = 'inguine_sx',
  SPALLA_DX = 'spalla_dx',
  SPALLA_SX = 'spalla_sx',
  ALTRO = 'altro',
}

export const PAIN_ZONES_LABELS: Record<PAIN_ZONES, string> = {
  [PAIN_ZONES.GINOCCHIO_DX]: 'Ginocchio DX',
  [PAIN_ZONES.GINOCCHIO_SX]: 'Ginocchio SX',
  [PAIN_ZONES.CAVIGLIA_DX]: 'Caviglia DX',
  [PAIN_ZONES.CAVIGLIA_SX]: 'Caviglia SX',
  [PAIN_ZONES.HAMSTRING_DX]: 'Hamstring DX',
  [PAIN_ZONES.HAMSTRING_SX]: 'Hamstring SX',
  [PAIN_ZONES.QUADRICIPITE_DX]: 'Quadricipite DX',
  [PAIN_ZONES.QUADRICIPITE_SX]: 'Quadricipite SX',
  [PAIN_ZONES.POLPACCIO_DX]: 'Polpaccio DX',
  [PAIN_ZONES.POLPACCIO_SX]: 'Polpaccio SX',
  [PAIN_ZONES.PIEDE_DX]: 'Piede DX',
  [PAIN_ZONES.PIEDE_SX]: 'Piede SX',
  [PAIN_ZONES.SCHIENA_LOMBARE]: 'Schiena lombare',
  [PAIN_ZONES.SCHIENA_DORSALE]: 'Schiena dorsale',
  [PAIN_ZONES.ANCA_DX]: 'Anca DX',
  [PAIN_ZONES.ANCA_SX]: 'Anca SX',
  [PAIN_ZONES.INGUINE_DX]: 'Inguine DX',
  [PAIN_ZONES.INGUINE_SX]: 'Inguine SX',
  [PAIN_ZONES.SPALLA_DX]: 'Spalla DX',
  [PAIN_ZONES.SPALLA_SX]: 'Spalla SX',
  [PAIN_ZONES.ALTRO]: 'Altro',
};
